import { useState, useMemo } from 'react';
import {
  Shield,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Building2,
  CreditCard,
  Train,
  Landmark,
  Calendar,
  FileText,
  Wrench,
  Code,
  Users,
  Clock,
  Flag,
  Copy,
  Check,
  ShieldAlert,
  ShieldCheck,
  Glasses,
} from 'lucide-react';
import type { EaaIssue, EaaCategory, EaaAffectedElement, IssueCategory } from '../../types';
import { ViewportBadge } from '../ui/ViewportBadge';
import { EaaFilters, EaaFiltersState, Severity } from './EaaFilters';
import { EaaCategorySection } from './EaaCategorySection';
import { TimeEstimateInput } from '../ui/TimeEstimateInput';

interface AcceptedRiskInfo {
  issueId: string;
  expiresAt: string;
}

interface TimeEstimateUpdateParams {
  scanId: string;
  pageUrl: string;
  issueType: IssueCategory;
  issueIdentifier: string;
  estimatedTimeToFix: number | null;
}

interface EaaComplianceProps {
  eaaIssues?: EaaIssue[];
  complianceScore?: number;
  loading?: boolean;
  onAcceptRisk?: (issue: EaaIssue) => void;
  acceptedRisks?: AcceptedRiskInfo[];
  scanId?: string;
  onTimeEstimateUpdate?: (params: TimeEstimateUpdateParams) => Promise<void>;
}

type ComplianceStatus = 'compliant' | 'partially-compliant' | 'non-compliant';

interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function CollapsibleSection({ title, icon, defaultOpen = false, children }: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-light/50 transition-colors text-left"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-3">
          <span className="text-coral">{icon}</span>
          <span className="text-heading-3 font-semibold">{title}</span>
        </div>
        {isOpen ? (
          <ChevronDown size={20} className="text-gray-medium" />
        ) : (
          <ChevronRight size={20} className="text-gray-medium" />
        )}
      </button>
      {isOpen && (
        <div className="border-t border-gray-light p-4">
          {children}
        </div>
      )}
    </div>
  );
}

interface SeverityBadgeProps {
  severity: 'critical' | 'serious' | 'moderate' | 'minor';
}

function SeverityBadge({ severity }: SeverityBadgeProps) {
  const config = {
    critical: {
      bg: 'bg-red-100',
      text: 'text-red-800',
      border: 'border-red-200',
      icon: <AlertTriangle size={14} />,
    },
    serious: {
      bg: 'bg-orange-100',
      text: 'text-orange-800',
      border: 'border-orange-200',
      icon: <AlertCircle size={14} />,
    },
    moderate: {
      bg: 'bg-yellow-100',
      text: 'text-yellow-800',
      border: 'border-yellow-200',
      icon: <Info size={14} />,
    },
    minor: {
      bg: 'bg-blue-100',
      text: 'text-blue-800',
      border: 'border-blue-200',
      icon: <Info size={14} />,
    },
  };

  const { bg, text, border, icon } = config[severity];

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-body-sm font-medium ${bg} ${text} border ${border}`}
    >
      {icon}
      {severity.charAt(0).toUpperCase() + severity.slice(1)}
    </span>
  );
}

function getComplianceStatus(score?: number, issueCount?: number): ComplianceStatus {
  if (score === undefined) {
    return issueCount === 0 || issueCount === undefined ? 'compliant' : 'non-compliant';
  }
  if (score >= 90) return 'compliant';
  if (score >= 50) return 'partially-compliant';
  return 'non-compliant';
}

function getComplianceStatusColor(status: ComplianceStatus): string {
  switch (status) {
    case 'compliant':
      return 'text-status-success';
    case 'partially-compliant':
      return 'text-status-warning';
    case 'non-compliant':
      return 'text-status-error';
  }
}

function getComplianceStatusBg(status: ComplianceStatus): string {
  switch (status) {
    case 'compliant':
      return 'bg-status-success';
    case 'partially-compliant':
      return 'bg-status-warning';
    case 'non-compliant':
      return 'bg-status-error';
  }
}

function getComplianceStatusLabel(status: ComplianceStatus): string {
  switch (status) {
    case 'compliant':
      return 'Compliant';
    case 'partially-compliant':
      return 'Partially Compliant';
    case 'non-compliant':
      return 'Non-Compliant';
  }
}

function getComplianceStatusIcon(status: ComplianceStatus) {
  switch (status) {
    case 'compliant':
      return <CheckCircle size={24} />;
    case 'partially-compliant':
      return <AlertCircle size={24} />;
    case 'non-compliant':
      return <AlertTriangle size={24} />;
  }
}

function SkeletonCard() {
  return (
    <div className="card animate-pulse">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 bg-gray-light rounded" />
        <div className="h-4 bg-gray-light rounded w-32" />
      </div>
      <div className="h-8 bg-gray-light rounded w-20 mb-2" />
      <div className="h-3 bg-gray-light rounded w-full mb-2" />
      <div className="h-3 bg-gray-light rounded w-3/4" />
    </div>
  );
}

function SkeletonStatus() {
  return (
    <div className="animate-pulse">
      <div className="flex items-center gap-4 mb-4">
        <div className="w-12 h-12 rounded-full bg-gray-light" />
        <div className="h-8 bg-gray-light rounded w-48" />
      </div>
      <div className="h-4 bg-gray-light rounded w-32" />
    </div>
  );
}

function CircularScoreGauge({ score, size = 100 }: { score: number; size?: number }) {
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const status = getComplianceStatus(score);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="transform -rotate-90"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-gray-light"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={`transition-all duration-500 ${
            status === 'compliant'
              ? 'stroke-status-success'
              : status === 'partially-compliant'
              ? 'stroke-status-warning'
              : 'stroke-status-error'
          }`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-heading-2 font-bold ${getComplianceStatusColor(status)}`}>
          {score}
        </span>
        <span className="text-caption text-gray-medium">/ 100</span>
      </div>
    </div>
  );
}

/**
 * Component to display affected elements with copy selector functionality
 */
function AffectedElementsList({
  elements,
}: {
  elements: { selector: string; html: string; pageUrl?: string; failureSummary?: string }[];
}) {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleCopySelector = async (selector: string, index: number) => {
    try {
      await navigator.clipboard.writeText(selector);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy selector:', err);
    }
  };

  return (
    <div>
      <h5 className="text-body-sm font-semibold text-dark-text mb-2">
        Affected Elements ({elements.length})
      </h5>
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {elements.map((element, index) => (
          <div key={index} className="bg-gray-100 p-2 rounded text-caption">
            <div className="flex items-center justify-between gap-2">
              <div className="font-mono text-coral truncate flex-1">{element.selector}</div>
              <button
                onClick={() => handleCopySelector(element.selector, index)}
                className="flex items-center gap-1 px-2 py-1 text-caption text-gray-medium hover:text-coral bg-white rounded border border-gray-light hover:border-coral transition-colors flex-shrink-0"
                title="Copy selector"
              >
                {copiedIndex === index ? (
                  <>
                    <Check size={12} className="text-status-success" />
                    <span className="text-status-success">Copied</span>
                  </>
                ) : (
                  <>
                    <Copy size={12} />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
            <pre className="mt-1 text-gray-600 overflow-x-auto whitespace-pre-wrap">
              {element.html}
            </pre>
            {element.failureSummary && (
              <div className="mt-1 text-gray-600 whitespace-pre-wrap">
                {element.failureSummary}
              </div>
            )}
            {element.pageUrl && (
              <div className="mt-1 text-gray-medium truncate">
                Page: {element.pageUrl}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

interface IssueCardProps {
  issue: EaaIssue;
  onAcceptRisk?: (issue: EaaIssue) => void;
  isRiskAccepted?: boolean;
  acceptedUntil?: string;
  scanId?: string;
  onTimeEstimateUpdate?: (params: TimeEstimateUpdateParams) => Promise<void>;
}

function IssueCard({ issue, onAcceptRisk, isRiskAccepted, acceptedUntil, scanId, onTimeEstimateUpdate }: IssueCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localTimeEstimate, setLocalTimeEstimate] = useState<number | undefined>(issue.estimatedTimeToFix);

  const handleTimeEstimateChange = async (minutes: number | null) => {
    if (onTimeEstimateUpdate && scanId && issue.pageUrl) {
      await onTimeEstimateUpdate({
        scanId,
        pageUrl: issue.pageUrl,
        issueType: 'EAA',
        issueIdentifier: issue.clauseNumber,
        estimatedTimeToFix: minutes,
      });
      setLocalTimeEstimate(minutes ?? undefined);
    }
  };

  return (
    <div className="border border-gray-light rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-start gap-4 p-4 hover:bg-gray-light/30 transition-colors text-left"
        aria-expanded={isExpanded}
      >
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {issue.ruleId && (
              <span className="text-body-sm font-mono text-coral bg-coral/10 px-2 py-0.5 rounded">
                {issue.ruleId}
              </span>
            )}
            {!issue.ruleId && (
              <span className="text-body-sm font-mono text-coral bg-coral/10 px-2 py-0.5 rounded">
                {issue.clauseNumber}
              </span>
            )}
            {issue.needsManualReview ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-body-sm font-medium bg-gray-100 text-gray-600 border border-gray-200">
                <Glasses size={14} />
                Manual review needed
              </span>
            ) : (
              <SeverityBadge severity={issue.severity} />
            )}
            {issue.viewport && (
              <ViewportBadge viewport={issue.viewport} />
            )}
            {issue.annexReference && (
              <span className="text-body-sm text-gray-medium">
                {issue.annexReference}
              </span>
            )}
            {isRiskAccepted && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-body-sm font-medium">
                <ShieldCheck size={12} />
                Risk Accepted
              </span>
            )}
            {localTimeEstimate && (
              <span className="text-body-sm text-gray-medium flex items-center gap-1">
                <Clock size={12} />
                {localTimeEstimate < 60 ? `${localTimeEstimate}m` : `${Math.floor(localTimeEstimate / 60)}h${localTimeEstimate % 60 > 0 ? ` ${localTimeEstimate % 60}m` : ''}`}
              </span>
            )}
          </div>
          <h4 className="text-body font-semibold text-dark-text mb-1">
            {issue.requirement}
          </h4>
          <p className="text-body-sm text-gray-medium line-clamp-2">
            {issue.description}
          </p>
          {/* WCAG Mapping */}
          {issue.wcagMapping && issue.wcagMapping.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {issue.wcagMapping.map((criteria) => (
                <span
                  key={criteria}
                  className="text-caption px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded"
                >
                  WCAG {criteria}
                </span>
              ))}
            </div>
          )}
          {/* Affected element count */}
          {issue.affectedElementCount && issue.affectedElementCount > 0 && (
            <div className="mt-2 text-caption text-gray-medium">
              {issue.affectedElementCount} {issue.affectedElementCount === 1 ? 'element' : 'elements'} affected
            </div>
          )}
        </div>
        {isExpanded ? (
          <ChevronDown size={20} className="text-gray-medium flex-shrink-0 mt-1" />
        ) : (
          <ChevronRight size={20} className="text-gray-medium flex-shrink-0 mt-1" />
        )}
      </button>
      {isExpanded && (
        <div className="border-t border-gray-light p-4 bg-gray-light/20 space-y-4">
          {/* Full description */}
          <div>
            <h5 className="text-body-sm font-semibold text-dark-text mb-1">
              Issue Details
            </h5>
            <p className="text-body-sm text-gray-medium">
              {issue.description}
            </p>
          </div>

          {/* Why it Matters (from new remediation guidance) */}
          {issue.remediationGuidance?.whyItMatters && (
            <div className="flex items-start gap-2">
              <Users size={18} className="text-coral flex-shrink-0 mt-0.5" />
              <div>
                <h5 className="text-body-sm font-semibold text-dark-text mb-1">
                  Why It Matters
                </h5>
                <p className="text-body-sm text-gray-medium">
                  {issue.remediationGuidance.whyItMatters}
                </p>
              </div>
            </div>
          )}

          {/* Affected Users */}
          {issue.remediationGuidance?.affectedUsers && issue.remediationGuidance.affectedUsers.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {issue.remediationGuidance.affectedUsers.map((user, index) => (
                <span
                  key={index}
                  className="text-caption px-2 py-1 bg-purple-100 text-purple-700 rounded"
                >
                  {user}
                </span>
              ))}
            </div>
          )}

          {/* WCAG Mapping Details */}
          {issue.wcagMapping && issue.wcagMapping.length > 0 && (
            <div>
              <h5 className="text-body-sm font-semibold text-dark-text mb-1">
                Mapped WCAG Criteria
              </h5>
              <div className="flex flex-wrap gap-2">
                {issue.wcagMapping.map((criteria) => (
                  <a
                    key={criteria}
                    href={`https://www.w3.org/WAI/WCAG21/Understanding/${criteria.toLowerCase().replace(/\./g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-body-sm px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 flex items-center gap-1"
                  >
                    WCAG {criteria}
                    <ExternalLink size={12} />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* How to Fix (from new remediation guidance) */}
          {issue.remediationGuidance?.howToFix && issue.remediationGuidance.howToFix.length > 0 && (
            <div className="flex items-start gap-2">
              <Wrench size={18} className="text-coral flex-shrink-0 mt-0.5" />
              <div>
                <h5 className="text-body-sm font-semibold text-dark-text mb-1">
                  How to Fix
                </h5>
                <ol className="text-body-sm text-gray-medium list-decimal list-inside space-y-1">
                  {issue.remediationGuidance.howToFix.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ol>
              </div>
            </div>
          )}

          {/* Legacy Remediation */}
          {!issue.remediationGuidance && issue.remediation && (
            <div className="flex items-start gap-2">
              <Wrench size={18} className="text-coral flex-shrink-0 mt-0.5" />
              <div>
                <h5 className="text-body-sm font-semibold text-dark-text mb-1">
                  Remediation Steps
                </h5>
                <p className="text-body-sm text-gray-medium whitespace-pre-line">
                  {issue.remediation}
                </p>
              </div>
            </div>
          )}

          {/* Code Example */}
          {issue.remediationGuidance?.codeExample && (
            <div className="flex items-start gap-2">
              <Code size={18} className="text-coral flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h5 className="text-body-sm font-semibold text-dark-text mb-2">
                  Code Example
                </h5>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <span className="text-caption font-medium text-red-600 mb-1 block">Before:</span>
                    <pre className="text-caption bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto">
                      <code>{issue.remediationGuidance.codeExample.before}</code>
                    </pre>
                  </div>
                  <div>
                    <span className="text-caption font-medium text-green-600 mb-1 block">After:</span>
                    <pre className="text-caption bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto">
                      <code>{issue.remediationGuidance.codeExample.after}</code>
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Effort and Priority */}
          {(issue.remediationGuidance?.estimatedEffort || issue.remediationGuidance?.priority) && (
            <div className="flex flex-wrap gap-4">
              {issue.remediationGuidance.estimatedEffort && (
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-gray-medium" />
                  <span className="text-caption text-gray-medium">
                    Effort:{' '}
                    <span className="font-medium text-dark-text capitalize">
                      {issue.remediationGuidance.estimatedEffort}
                    </span>
                  </span>
                </div>
              )}
              {issue.remediationGuidance.priority && (
                <div className="flex items-center gap-2">
                  <Flag size={14} className="text-gray-medium" />
                  <span className="text-caption text-gray-medium">
                    Priority:{' '}
                    <span className={`font-medium capitalize ${
                      issue.remediationGuidance.priority === 'immediate' ? 'text-red-600' :
                      issue.remediationGuidance.priority === 'quick-win' ? 'text-green-600' :
                      'text-blue-600'
                    }`}>
                      {issue.remediationGuidance.priority.replace('-', ' ')}
                    </span>
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Affected Elements */}
          {issue.affectedElements && issue.affectedElements.length > 0 && (
            <AffectedElementsList elements={issue.affectedElements} />
          )}

          {/* Legacy Element */}
          {!issue.affectedElements && issue.element && (
            <div>
              <h5 className="text-body-sm font-semibold text-dark-text mb-2">
                Affected Element
              </h5>
              <pre className="text-caption bg-gray-100 p-2 rounded overflow-x-auto">
                {issue.element}
              </pre>
            </div>
          )}

          {/* Affected pages */}
          {issue.affectedPages && issue.affectedPages.length > 0 && (
            <div>
              <h5 className="text-body-sm font-semibold text-dark-text mb-2">
                Affected pages ({issue.affectedPages.length})
              </h5>
              <ul className="space-y-1 max-h-48 overflow-y-auto">
                {issue.affectedPages.map((url) => (
                  <li key={url}>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-caption text-coral hover:text-coral/80 break-all"
                    >
                      {url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Chapter 9 clauses are derived from automated accessibility testing.
              Rather than duplicate the element-level detail here, point to the
              Accessibility tab, which is the single source of truth for it. */}
          {issue.clauseNumber?.startsWith('9.') && (
            <div className="p-3 bg-blue-500/5 border border-blue-500/20 rounded-card">
              <p className="text-caption text-gray-medium">
                Detected via automated accessibility testing (axe-core), mapped from
                <span className="font-medium text-dark-text"> WCAG {issue.clauseNumber.slice(2)}</span>.
                For the exact elements and how to fix, see the <span className="font-medium text-dark-text">Accessibility</span> tab.
              </p>
            </div>
          )}

          {/* Help URL */}
          {issue.helpUrl && (
            <a
              href={issue.helpUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-body-sm text-coral hover:text-coral/80"
            >
              Learn more
              <ExternalLink size={14} />
            </a>
          )}

          {/* Time Estimate (Admin editable) */}
          {scanId && onTimeEstimateUpdate && issue.pageUrl && (
            <div className="flex items-start gap-2">
              <Clock size={18} className="text-coral flex-shrink-0 mt-0.5" />
              <div>
                <h5 className="text-body-sm font-semibold text-dark-text mb-2">
                  Estimated Time to Fix
                </h5>
                <TimeEstimateInput
                  value={localTimeEstimate ?? null}
                  onChange={handleTimeEstimateChange}
                />
              </div>
            </div>
          )}

          {/* Risk Acceptance Section */}
          <div className="pt-3 mt-3 border-t border-gray-light">
            {isRiskAccepted ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-body-sm text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                  <ShieldCheck size={16} />
                  <span>
                    Risk accepted until{' '}
                    <span className="font-medium">
                      {acceptedUntil ? new Date(acceptedUntil).toLocaleDateString() : 'N/A'}
                    </span>
                  </span>
                </div>
              </div>
            ) : onAcceptRisk ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAcceptRisk(issue);
                }}
                className="flex items-center gap-2 px-3 py-2 text-body-sm text-amber-700 border border-amber-300 rounded-lg hover:bg-amber-50 transition-colors"
              >
                <ShieldAlert size={16} />
                Accept Risk
              </button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

// EAA Information content
const EAA_INFO = {
  deadline: 'June 28, 2025',
  scope: 'Products and services placed on the EU market',
  basedOn: 'EN 301 549 / WCAG 2.1 Level AA',
  coveredSectors: [
    {
      icon: Building2,
      name: 'E-commerce',
      description: 'Online shops and marketplaces',
    },
    {
      icon: CreditCard,
      name: 'Banking & Finance',
      description: 'Financial services and payment terminals',
    },
    {
      icon: Train,
      name: 'Transport',
      description: 'Ticketing systems and travel information',
    },
    {
      icon: Landmark,
      name: 'Public Sector',
      description: 'Government websites and services',
    },
  ],
  resources: [
    {
      title: 'European Accessibility Act (EAA)',
      url: 'https://ec.europa.eu/social/main.jsp?catId=1202',
      source: 'European Commission',
    },
    {
      title: 'EN 301 549 Standard',
      url: 'https://www.etsi.org/deliver/etsi_en/301500_301599/301549/03.02.01_60/en_301549v030201p.pdf',
      source: 'ETSI',
    },
    {
      title: 'WCAG 2.1 Guidelines',
      url: 'https://www.w3.org/WAI/WCAG21/quickref/',
      source: 'W3C WAI',
    },
  ],
};

// Default category for issues without category
const DEFAULT_CATEGORY: EaaCategory = 'common-pitfalls';

// All categories in display order
const ALL_CATEGORIES: EaaCategory[] = [
  'keyboard-navigation',
  'screen-reader-support',
  'focus-management',
  'color-contrast',
  'headings-structure',
  'aria-semantics',
  'links-buttons',
  'interactive-elements',
  'forms',
  'media',
  'common-pitfalls',
  'skip-links-landmarks',
  'mobile-responsive',
  'cookies-consent',
];

export function EaaCompliance({
  eaaIssues,
  complianceScore,
  loading = false,
  onAcceptRisk,
  acceptedRisks = [],
  scanId,
  onTimeEstimateUpdate,
}: EaaComplianceProps) {
  // Filter state
  const [filters, setFilters] = useState<EaaFiltersState>({
    severities: [],
    categories: [],
    viewports: [],
    pages: [],
    searchQuery: '',
    deduplicate: false,
  });

  // Get unique pages from issues
  const availablePages = useMemo(() => {
    if (!eaaIssues) return [];
    const pages = new Set<string>();
    eaaIssues.forEach((issue) => {
      if (issue.pageUrl) pages.add(issue.pageUrl);
    });
    return Array.from(pages).sort();
  }, [eaaIssues]);

  // Filter issues based on filter state
  const filteredIssues = useMemo(() => {
    if (!eaaIssues) return [];

    const acceptedIds = new Set(acceptedRisks.map((r) => r.issueId));

    return eaaIssues.filter((issue) => {
      // Exclude issues with an active risk acceptance (reviewed / accepted)
      if (acceptedIds.has(issue.ruleId || issue.clauseNumber || '')) return false;

      // Severity filter
      if (filters.severities.length > 0 && !filters.severities.includes(issue.severity as Severity)) {
        return false;
      }

      // Category filter
      if (filters.categories.length > 0) {
        const issueCategory = issue.category || DEFAULT_CATEGORY;
        if (!filters.categories.includes(issueCategory)) {
          return false;
        }
      }

      // Viewport filter
      if (filters.viewports.length > 0 && issue.viewport) {
        if (!filters.viewports.includes(issue.viewport)) {
          return false;
        }
      }

      // Page filter
      if (filters.pages.length > 0 && issue.pageUrl) {
        if (!filters.pages.includes(issue.pageUrl)) {
          return false;
        }
      }

      // Search filter
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        const searchText = [
          issue.ruleId,
          issue.clauseNumber,
          issue.requirement,
          issue.description,
          issue.annexReference,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (!searchText.includes(query)) {
          return false;
        }
      }

      return true;
    });
  }, [eaaIssues, filters, acceptedRisks]);

  // Deduplicate issues when enabled
  const deduplicatedIssues = useMemo(() => {
    if (!filters.deduplicate) return filteredIssues;

    // Group by ruleId or clauseNumber
    const groups = new Map<string, {
      issue: EaaIssue;
      occurrences: number;
      pages: Set<string>;
      allElements: EaaAffectedElement[];
    }>();

    filteredIssues.forEach((issue) => {
      const key = issue.ruleId || issue.clauseNumber;
      const existing = groups.get(key);

      if (existing) {
        existing.occurrences++;
        if (issue.pageUrl) existing.pages.add(issue.pageUrl);
        if (issue.affectedElements) {
          existing.allElements.push(...issue.affectedElements);
        }
      } else {
        groups.set(key, {
          issue,
          occurrences: 1,
          pages: new Set(issue.pageUrl ? [issue.pageUrl] : []),
          allElements: [...(issue.affectedElements || [])],
        });
      }
    });

    // Convert back to issues with occurrence metadata
    return Array.from(groups.values()).map(({ issue, occurrences, pages, allElements }) => ({
      ...issue,
      // Add occurrence count in the description
      description: occurrences > 1
        ? `${issue.description} (${occurrences} occurrences across ${pages.size} page${pages.size !== 1 ? 's' : ''})`
        : issue.description,
      affectedElements: allElements.slice(0, 20), // Limit to 20 elements
      affectedElementCount: allElements.length,
      affectedPages: Array.from(pages).sort(),
    }));
  }, [filteredIssues, filters.deduplicate]);

  // Group issues by category
  const issuesByCategory = useMemo(() => {
    const groups = new Map<EaaCategory, EaaIssue[]>();

    // Initialize all categories with empty arrays
    ALL_CATEGORIES.forEach((category) => {
      groups.set(category, []);
    });

    // Group deduplicated issues
    deduplicatedIssues.forEach((issue) => {
      const category = issue.category || DEFAULT_CATEGORY;
      const existing = groups.get(category) || [];
      groups.set(category, [...existing, issue]);
    });

    // Return only categories with issues
    return new Map(
      Array.from(groups.entries()).filter(([, issues]) => issues.length > 0)
    );
  }, [deduplicatedIssues]);

  // Calculate severity counts per category
  const categorySeverityCounts = useMemo(() => {
    const counts = new Map<EaaCategory, { critical: number; serious: number; moderate: number; minor: number }>();

    issuesByCategory.forEach((issues, category) => {
      const severityCounts = { critical: 0, serious: 0, moderate: 0, minor: 0 };
      issues.forEach((issue) => {
        if (issue.needsManualReview) return;
        severityCounts[issue.severity]++;
      });
      counts.set(category, severityCounts);
    });

    return counts;
  }, [issuesByCategory]);

  // Calculate issue severity counts
  const issueCounts = useMemo(() => {
    if (!eaaIssues) return { critical: 0, serious: 0, moderate: 0, minor: 0, total: 0 };

    const acceptedIds = new Set(acceptedRisks.map((r) => r.issueId));
    return eaaIssues.reduce(
      (acc, issue) => {
        if (issue.needsManualReview) return acc;
        if (acceptedIds.has(issue.ruleId || issue.clauseNumber || '')) return acc;
        acc[issue.severity]++;
        acc.total++;
        return acc;
      },
      { critical: 0, serious: 0, moderate: 0, minor: 0, total: 0 }
    );
  }, [eaaIssues, acceptedRisks]);

  const complianceStatus = getComplianceStatus(complianceScore, issueCounts.total);

  if (loading) {
    return (
      <div className="space-y-8">
        {/* Loading Status */}
        <div className="card">
          <h3 className="text-heading-3 font-semibold mb-6 flex items-center gap-2">
            <Shield className="text-coral" size={24} />
            EAA Compliance Status
          </h3>
          <SkeletonStatus />
        </div>

        {/* Loading Issues */}
        <div>
          <h3 className="text-heading-3 font-semibold mb-4">Compliance Issues</h3>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Compliance Status Header */}
      <div className="card">
        <h3 className="text-heading-3 font-semibold mb-6 flex items-center gap-2">
          <Shield className="text-coral" size={24} />
          EAA Compliance Status
        </h3>

        <div className="flex flex-col lg:flex-row items-center gap-8">
          {/* Score Gauge */}
          <div className="flex flex-col items-center">
            {complianceScore !== undefined ? (
              <>
                <CircularScoreGauge score={complianceScore} />
                <p className="text-body-sm text-gray-medium mt-3">Compliance Score</p>
              </>
            ) : (
              <div className="text-center">
                <div className="w-[100px] h-[100px] rounded-full border-8 border-gray-light flex items-center justify-center">
                  <span className="text-heading-3 text-gray-medium">N/A</span>
                </div>
                <p className="text-body-sm text-gray-medium mt-3">No score available</p>
              </div>
            )}
          </div>

          {/* Status and Summary */}
          <div className="flex-1 text-center lg:text-left">
            <div className="flex items-center justify-center lg:justify-start gap-3 mb-4">
              <div className={`p-2 rounded-full ${getComplianceStatusBg(complianceStatus)}/10`}>
                <span className={getComplianceStatusColor(complianceStatus)}>
                  {getComplianceStatusIcon(complianceStatus)}
                </span>
              </div>
              <h4 className={`text-heading-2 font-bold ${getComplianceStatusColor(complianceStatus)}`}>
                {getComplianceStatusLabel(complianceStatus)}
              </h4>
            </div>

            {/* Issue Summary */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-body-sm">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-severity-critical" />
                <span className="font-medium">{issueCounts.critical}</span>
                <span className="text-gray-medium">Critical</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-severity-serious" />
                <span className="font-medium">{issueCounts.serious}</span>
                <span className="text-gray-medium">Serious</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-severity-moderate" />
                <span className="font-medium">{issueCounts.moderate}</span>
                <span className="text-gray-medium">Moderate</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-severity-minor" />
                <span className="font-medium">{issueCounts.minor}</span>
                <span className="text-gray-medium">Minor</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* EAA Information Section */}
      <CollapsibleSection
        title="About the European Accessibility Act"
        icon={<Info size={20} />}
        defaultOpen={false}
      >
        <div className="space-y-6">
          {/* Key Information */}
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="p-4 bg-coral/5 rounded-lg border border-coral/10">
              <div className="flex items-center gap-2 text-coral mb-2">
                <Calendar size={18} />
                <span className="text-body-sm font-semibold">Deadline</span>
              </div>
              <p className="text-body font-bold text-dark-text">{EAA_INFO.deadline}</p>
              <p className="text-caption text-gray-medium">For products and services</p>
            </div>
            <div className="p-4 bg-gray-light rounded-lg">
              <div className="flex items-center gap-2 text-gray-medium mb-2">
                <FileText size={18} />
                <span className="text-body-sm font-semibold">Scope</span>
              </div>
              <p className="text-body-sm text-dark-text">{EAA_INFO.scope}</p>
            </div>
            <div className="p-4 bg-gray-light rounded-lg">
              <div className="flex items-center gap-2 text-gray-medium mb-2">
                <Shield size={18} />
                <span className="text-body-sm font-semibold">Based On</span>
              </div>
              <p className="text-body-sm text-dark-text">{EAA_INFO.basedOn}</p>
            </div>
          </div>

          {/* Covered Sectors */}
          <div>
            <h4 className="text-body font-semibold text-dark-text mb-3">
              Covered Sectors
            </h4>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {EAA_INFO.coveredSectors.map((sector) => {
                const Icon = sector.icon;
                return (
                  <div
                    key={sector.name}
                    className="flex items-start gap-3 p-3 bg-gray-light/50 rounded-lg"
                  >
                    <Icon size={20} className="text-coral flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-body-sm font-medium text-dark-text block">
                        {sector.name}
                      </span>
                      <span className="text-caption text-gray-medium">
                        {sector.description}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Official Resources */}
          <div>
            <h4 className="text-body font-semibold text-dark-text mb-3">
              Official Resources
            </h4>
            <div className="grid sm:grid-cols-3 gap-3">
              {EAA_INFO.resources.map((resource) => (
                <a
                  key={resource.title}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 p-3 bg-gray-light/50 rounded-lg hover:bg-gray-light transition-colors group"
                >
                  <ExternalLink
                    size={18}
                    className="text-gray-medium group-hover:text-coral transition-colors flex-shrink-0 mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="text-body-sm font-medium text-dark-text group-hover:text-coral transition-colors block">
                      {resource.title}
                    </span>
                    <span className="text-caption text-gray-medium">
                      {resource.source}
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Issues Section */}
      {issueCounts.total > 0 ? (
        <div>
          <h3 className="text-heading-3 font-semibold mb-4 flex items-center gap-2">
            <AlertTriangle className="text-coral" size={24} />
            Compliance Issues ({issueCounts.total})
          </h3>

          {/* Filters */}
          <div className="mb-6">
            <EaaFilters
              filters={filters}
              onFiltersChange={setFilters}
              availablePages={availablePages}
              issueCount={issueCounts.total}
              filteredCount={deduplicatedIssues.length}
            />
          </div>

          {/* Issues grouped by category */}
          <div className="space-y-4">
            {Array.from(issuesByCategory.entries()).map(([category, issues]) => (
              <EaaCategorySection
                key={category}
                category={category}
                issueCount={issues.length}
                severityCounts={categorySeverityCounts.get(category) || { critical: 0, serious: 0, moderate: 0, minor: 0 }}
                defaultExpanded={issues.some((i) => i.severity === 'critical')}
              >
                <div className="space-y-3">
                  {issues.map((issue, index) => {
                    const issueId = issue.ruleId || issue.clauseNumber;
                    const acceptedRisk = acceptedRisks.find(r => r.issueId === issueId);
                    return (
                      <IssueCard
                        key={`${issueId}-${index}`}
                        issue={issue}
                        onAcceptRisk={onAcceptRisk}
                        isRiskAccepted={!!acceptedRisk}
                        acceptedUntil={acceptedRisk?.expiresAt}
                        scanId={scanId}
                        onTimeEstimateUpdate={onTimeEstimateUpdate}
                      />
                    );
                  })}
                </div>
              </EaaCategorySection>
            ))}
          </div>

          {/* No results after filtering */}
          {filteredIssues.length === 0 && issueCounts.total > 0 && (
            <div className="card text-center py-8">
              <Info size={32} className="mx-auto text-gray-medium opacity-50 mb-3" />
              <h4 className="text-body font-semibold text-dark-text mb-1">
                No matching issues
              </h4>
              <p className="text-body-sm text-gray-medium">
                Try adjusting your filters to see more results.
              </p>
            </div>
          )}
        </div>
      ) : (
        /* Empty State */
        <div className="card text-center py-12">
          <CheckCircle size={48} className="mx-auto text-status-success opacity-50 mb-4" />
          <h3 className="text-heading-3 font-semibold text-dark-text mb-2">
            No EAA Compliance Issues Found
          </h3>
          <p className="text-body text-gray-medium">
            {eaaIssues === undefined
              ? 'EAA compliance scanning was not included in this scan.'
              : 'Great work! No European Accessibility Act compliance issues were detected.'}
          </p>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-6 text-body-sm">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-status-success" />
          <span className="text-gray-medium">Compliant (90-100)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-status-warning" />
          <span className="text-gray-medium">Partially Compliant (50-89)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-status-error" />
          <span className="text-gray-medium">Non-Compliant (0-49)</span>
        </div>
      </div>
    </div>
  );
}
